import { useCallback, useEffect, useRef, useState } from "react";

import {
  type ResolvedVideoProfile,
  resolveVideoProfile,
  type VideoProfile,
} from "./video-profile";

export interface UseVideoProfileReturn {
  /** The probed profile; null until the probe settles. */
  profile: VideoProfile | null;
  /** The probe result behind `profile`, for telemetry. */
  resolved: ResolvedVideoProfile | null;
  /**
   * Resolves to the probed profile, running the probe at most once. Callers
   * that must not act on an unprobed profile await this rather than reading
   * `profile`.
   */
  getProfile: () => Promise<VideoProfile>;
}

/**
 * Probes the video profile once and exposes it two ways: as state, for render,
 * and as a promise, for callers that have to wait for it.
 *
 * `profile` is null until the probe settles. Anything rendered before then
 * should assume `STANDARD_VIDEO_PROFILE` — the same assumption the probe makes
 * whenever it cannot show that profile is unusable.
 */
export function useVideoProfile(): UseVideoProfileReturn {
  const promiseRef = useRef<Promise<VideoProfile> | null>(null);
  const [resolved, setResolved] = useState<ResolvedVideoProfile | null>(null);

  const getProfile = useCallback((): Promise<VideoProfile> => {
    promiseRef.current ??= resolveVideoProfile().then((result) => {
      setResolved(result);
      return result.profile;
    });
    return promiseRef.current;
  }, []);

  // Probe on mount so it has settled before the first stream start.
  useEffect(() => {
    void getProfile();
  }, [getProfile]);

  return { profile: resolved?.profile ?? null, resolved, getProfile };
}
